import { makeAutoObservable } from "mobx";
import { IChangeable } from "../../../stores/types";
import {AMOUNT_TYPE, FEE_AMOUNT_OPTIONS, FEE_TYPE, FEE_TYPE_MAP, IPolicyFee, SHIPPING_LABEL_FEE_TYPES} from "../policy-types";
import PolicyStore from "./policy-store";
import { FEE_TYPE_NON_EDITABLE } from "../constants";
import currentPortalStore from "../../../stores/current-portal-store";
import { FEE_TYPES } from "../../../models/User";
import { IInputValitationResponse } from "../../../hooks/input-validation-hook";

interface IPolicyFeeChanges extends Partial<IPolicyFee> {};

class PolicyFeeStore implements IChangeable<IPolicyFee> {
    private _model: IPolicyFee = {} as IPolicyFee;
    private _changes: IPolicyFeeChanges = {};

    constructor(model: IPolicyFee | undefined, private _defaults: IPolicyFee, private _policy: PolicyStore) {
        makeAutoObservable(this, {}, { autoBind: true });
        if (model) {
            this._setModel(model);
        }
    }

    private _setModel(model: IPolicyFee): void {
        this._model = ensureValidModelCopy(model);
    }

    public setChanges(changes: IPolicyFeeChanges): void {
        delete changes.id;        
        this._changes = changes;
    }

    public applyDefaultsIfNeeded(): void {
        /** when there is no model - setting defaults as changes */
        if (!this._model.id) {
            this._changes = { ...this._defaults };
        }
    }

    public alignFeeType(): void {
        /** if current feeType is NOT for shipping label - return, only shipping label has interchangeable types */
        if (this.feeType !== null && !SHIPPING_LABEL_FEE_TYPES.includes(this.feeType)) {
            return;
        }

        const currentFeeTypeOption = this.shippingLabelOptions.find(
            o =>
                (o.value === FEE_TYPE.FREE && this.feeType === null) ||
                o.value === this.feeType
        );
        if (!currentFeeTypeOption || currentFeeTypeOption.disabled) {
            this.setFeeType(null);
        }
    }

    public get id(): number { return this._model.id; }
    public get isCharged(): boolean { return this._changes.is_charged ?? this._model.is_charged; }
    public get isChargeTax(): boolean|null {         
        return this._changes.charge_tax ?? this._model.charge_tax; 
    }
    public get feeType(): FEE_TYPE | null {
        if ('fee_type' in this._changes) {
            if (!this._changes.fee_type) {
                return null;
            }
            return this._changes.fee_type;
        }
        return this._model.fee_type;
    }
    public get amount(): number { return this._changes.amount ?? this._model.amount; }
    public get amountType(): string { return this._changes.amount_type ?? this._model.amount_type; }

    public get policy() { return this._policy; }

    public get deleted(): boolean { return this._changes._deleted ?? false; }

    public validateFee(): IInputValitationResponse {
        let isValid: boolean = true;
        let messages: string[] = [];

        if (this.amount <= 0) {
            isValid = false;
            messages.push('The amount has to be greater, than 0');
        }

        return {
            isValid: isValid,
            messages: messages,
        }
    };
    
    public setDeleted(v: boolean): void {
        this._changes._deleted = v;
        if (!v) {
            delete this._changes._deleted;
        }
    }

    public setIsCharged(value: boolean) {
        this._changes.is_charged = value;

        this._changes.amount_type = AMOUNT_TYPE.FIXED;
        this._changes.amount = 0;
        this._changes.charge_tax = this._getDefaultChargeTax();

        this._clearUnneededChanges();
    }

    private _getDefaultChargeTax(): boolean {
        let defaultChargeTax = false;

        const returngoFeesSettings = currentPortalStore.setting?.returngoFeesSetting;
        returngoFeesSettings?.forEach(returngoFeesSetting => {
            switch (returngoFeesSetting.feeType) {
                case FEE_TYPES.RETURN_LABEL_FEE:
                    if (this.feeType === null || SHIPPING_LABEL_FEE_TYPES.includes(this.feeType)) {
                        defaultChargeTax = returngoFeesSetting.addTax;
                    }
                    break;
                case FEE_TYPES.RETURN_FEE:
                    if (this.feeType === FEE_TYPE.RETURN_FEE) {
                        defaultChargeTax = returngoFeesSetting.addTax;
                    }
                    break;
                case FEE_TYPES.RESTOCKING_FEE:
                    if (this.feeType === FEE_TYPE.RESTOCKING_FEE) {
                        defaultChargeTax = returngoFeesSetting.addTax;
                    }
                    break;
                case FEE_TYPES.CANCELLATION_FEE:
                    if (this.feeType === FEE_TYPE.CANCELLATION_FEE) {
                        defaultChargeTax = returngoFeesSetting.addTax;
                    }
                    break;
            }
        });

        return defaultChargeTax;
    }

    public setIsChargeTax(value: boolean) {
        this._changes.charge_tax = value;
        this._clearUnneededChanges();
    }

    private _isFeeTypeEditable(type: FEE_TYPE | null) {
        if (!type) {
            return true;
        }
        return FEE_TYPE_NON_EDITABLE.indexOf(type) === -1;
    }

    public setFeeType(value: FEE_TYPE | null) {
        if (!this._isFeeTypeEditable(this._model.fee_type)) {
            throw new Error(`Fee type ${this._model.fee_type} cannot be changed to ${value}`);
        }
        this._changes.fee_type = value;

        if (this._model.fee_type === value) {
            this.setIsCharged(this._model.is_charged);
            return;
        }

        const isChargedToUpdate = value === null || value === FEE_TYPE.LABEL_NOT_PROVIDED ? false : true;
        this.setIsCharged(isChargedToUpdate);
    }

    public setAmount(value: number) {
        this._changes.amount = value;
        this._clearUnneededChanges();
    }

    public setAmountType(value: AMOUNT_TYPE) {
        this._changes.amount_type = value;
        if (this._model.amount_type === value) {
            this.setAmount(this._model.amount);
        } else {
            this.setAmount(0);
        }
        this._clearUnneededChanges();
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IPolicyFee;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

    public get hasPercentageAmount(): boolean {
        if (!this.hasEditableAmount) {
            return false;
        }
        return this.amountType === AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE ||
            this.amountType === AMOUNT_TYPE.ITEM_PERCENTAGE_CATALOG_PRICE;
    }

    public get hasEditableAmount(): boolean {
        return !(
            this.feeType === FEE_TYPE.COST_PLUS_SHIPMENT_FEE ||
            this.feeType === FEE_TYPE.SHIPMENT_FEE
        );
    }

    public get shippingLabelOptions() {
        return this.getShippingLabelOptions();
    }

    public getShippingLabelOptions(supportedTypes?: FEE_TYPE[]) {
        const isLabelCostSupported = this.policy.isLabelCostSupported;
        const isShipByAnyCarrier = this.policy.isShipByAnyCarrier;

        const options = [
            {
                label: 'Free',
                value: FEE_TYPE.FREE, /** special case, DB treat null as valid value in this field */
            },
            {
                label: 'Cost',
                value: FEE_TYPE.COST,
                disabled: isShipByAnyCarrier || !isLabelCostSupported,
            },
            {
                label: 'Cost + per item fee',
                value: FEE_TYPE.COST_PLUS_ITEM_FEE,
                disabled: isShipByAnyCarrier || !isLabelCostSupported,
            },
            {
                label: 'Cost + per shipment fee',
                value: FEE_TYPE.COST_PLUS_SHIPMENT_FEE,
                disabled: isShipByAnyCarrier || !isLabelCostSupported,
            },
            {
                label: 'Charge fixed amount',
                value: FEE_TYPE.SHIPMENT_FEE,
            }
        ];

        if (isShipByAnyCarrier) {
            options.push({
                label: 'Not provided',
                value: FEE_TYPE.LABEL_NOT_PROVIDED,
            });
        }

        if (!supportedTypes) {
            return options;
        }

        return options.filter(option => supportedTypes.includes(option.value));
    }

    public get amountOptions() {
        const options = FEE_AMOUNT_OPTIONS;
        if (this.feeType === FEE_TYPE.RETURN_FEE) {
            return options.filter(o => [
                AMOUNT_TYPE.FIXED, AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE
            ].includes(o.value));
        }
        return options;
    }

    public get showReturnMethodNotSupportCostFee(): boolean {
        return !this.policy.isShipByAnyCarrier && !this.policy.isLabelCostSupported;
    }

    hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }
    discardChanges(): void {
        this._changes = {};
    }

    getUpdatedModel(): IPolicyFee|undefined {
        if (!this._model.id && (this.deleted || !this.hasChanges())) {
            return undefined;
        }

        /**
         * This should not happen, because if we have Charge additional fees toggle ON,
         * we must have policy fee store fee_type set in one of ways: or by toggle setter from defaults,
         * or loaded from database record. But there can be situations with not fully correct database,
         * when  Charge additional fees toggle is ON, but no policy_fees records, so the fee store will return not full model
         * from getUpdatedModel, fee_type can be missing, but it is important for any fee store and required by validation
         */
        /** when there is no model for this fee - we need to make sure that we set fee_type on changes */
        if (this.feeType === undefined) {
            this._changes.fee_type = this._defaults.fee_type;
        }

        const merged = { ...this._model, ...this._changes };
        const feeTypeForApi =
            merged.fee_type === FEE_TYPE.FREE ? null : merged.fee_type;

        const updatedModel = {
            ...merged,
            fee_type: feeTypeForApi,
            _has_changes: this.hasChanges(),
        };
        return updatedModel;
    }

    public getDisplayString(): string|undefined {
        if (!this.isCharged) {
            return undefined;
        }

        return FEE_TYPE_MAP[this.feeType as FEE_TYPE];
    }
}

export default PolicyFeeStore;

function ensureValidModelCopy(data: IPolicyFee): IPolicyFee {
    const toBool = (v: undefined | any) => Boolean(v);

    return {
        ...data,
        is_charged: toBool(data.is_charged),
        charge_tax: toBool(data.charge_tax),
    };
}
